import { useEffect, useRef, type FC, type PropsWithChildren } from "react";
import { getLastTabDirection } from "@platform/btx-grid-components";

const PRIMARY_BUTTON = 0;

export interface FocusableRateCellWrapperProps {
  /** The AG Grid cell DOM element (from ICellRendererParams.eGridCell) */
  eGridCell: HTMLElement;
}

/**
 * Wrapper component that handles focus delegation for rate cell renderers
 *
 * When the AG Grid cell receives focus (e.g., from Tab navigation from PlanDesigner),
 * this component automatically focuses the first input inside the cell.
 *
 * This enables seamless keyboard navigation between grids:
 * - Tab from PlanDesigner last cell → RatesGrid cell → first input in that cell
 * - Shift+Tab from RatesGrid first input → back to PlanDesigner
 *
 * Click behaviour: AG Grid intercepts mousedown and moves focus to the ag-cell
 * element instead of the clicked input. We track which input the user originally
 * clicked so the rAF focus-redirect lands on that specific input rather than
 * always defaulting to the first one.
 */
export const FocusableRateCellWrapper: FC<
  PropsWithChildren<FocusableRateCellWrapperProps>
> = ({ eGridCell, children }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mouseDownTargetRef = useRef<HTMLInputElement | null>(null);
  const contextMenuReturnRef = useRef<{
    input: HTMLInputElement | HTMLTextAreaElement | null;
  } | null>(null);

  useEffect(() => {
    if (!eGridCell) return;

    /**
     * Record which input the user clicked before AG Grid steals focus to the
     * ag-cell element. Only capture when no input inside this cell is already
     * focused — in that case the subsequent click goes directly to the target
     * input and the ag-cell does not receive a new focus event, so we must not
     * store a stale target that would be consumed by the next Tab-in navigation.
     */
    const handleMouseDown = (event: MouseEvent): void => {
      if (event.button === PRIMARY_BUTTON) {
        contextMenuReturnRef.current = null;
      }

      const activeElement = document.activeElement;
      const isInputInsideCellFocused =
        activeElement instanceof HTMLInputElement &&
        (containerRef.current?.contains(activeElement) ?? false);

      if (isInputInsideCellFocused) {
        mouseDownTargetRef.current = null;
        return;
      }

      if (
        event.target instanceof HTMLInputElement &&
        containerRef.current?.contains(event.target)
      ) {
        mouseDownTargetRef.current = event.target;
      } else {
        mouseDownTargetRef.current = null;
      }
    };

    const handleContextMenu = (event: MouseEvent): void => {
      const clickedInput =
        event.target instanceof Element
          ? event.target.closest<HTMLInputElement | HTMLTextAreaElement>(
              "input, textarea",
            )
          : null;
      const input =
        clickedInput && containerRef.current?.contains(clickedInput)
          ? clickedInput
          : null;

      contextMenuReturnRef.current = { input };
    };

    const returnFromContextMenu = (menuReturn: {
      input: HTMLInputElement | HTMLTextAreaElement | null;
    }): void => {
      requestAnimationFrame(() => {
        if (document.activeElement !== eGridCell) {
          return;
        }

        contextMenuReturnRef.current = null;
        menuReturn.input?.focus();
      });
    };

    /**
     * When the cell receives focus (from AG Grid navigation or a first click),
     * delegate focus to the appropriate input inside the cell.
     */
    const handleCellFocus = (event: FocusEvent): void => {
      // Only handle focus if the cell itself received focus (not an input inside it)
      if (event.target !== eGridCell) {
        return;
      }

      const menuReturn = contextMenuReturnRef.current;
      if (menuReturn) {
        mouseDownTargetRef.current = null;
        returnFromContextMenu(menuReturn);
        return;
      }

      const inputs = containerRef.current?.querySelectorAll<
        HTMLInputElement | HTMLTextAreaElement
      >("input, textarea");

      if (!inputs || inputs.length === 0) {
        return;
      }

      // Consume the pending mouse target (if any) and reset.
      const mouseTarget = mouseDownTargetRef.current;
      mouseDownTargetRef.current = null;

      // For a click: use the input the user actually clicked.
      // For keyboard (Tab/Shift+Tab): use first or last based on direction.
      const targetInput =
        mouseTarget !== null && containerRef.current?.contains(mouseTarget)
          ? mouseTarget
          : getLastTabDirection() === "backward"
            ? inputs[inputs.length - 1]
            : inputs[0];

      // Use requestAnimationFrame to ensure the focus happens after AG Grid's focus handling
      requestAnimationFrame(() => {
        // Something outside the cell may have taken focus in the meantime — a
        // dialog opened from the cell's context menu, whose focus trap pulls
        // focus back in. Delegating now would fight it for every keystroke.
        if (document.activeElement !== eGridCell) {
          return;
        }

        targetInput.focus();
        targetInput.select();
      });
    };

    eGridCell.addEventListener("mousedown", handleMouseDown);
    eGridCell.addEventListener("contextmenu", handleContextMenu);
    eGridCell.addEventListener("focus", handleCellFocus);

    return () => {
      eGridCell.removeEventListener("mousedown", handleMouseDown);
      eGridCell.removeEventListener("contextmenu", handleContextMenu);
      eGridCell.removeEventListener("focus", handleCellFocus);
    };
  }, [eGridCell]);

  return (
    <div ref={containerRef} style={{ width: "100%", height: "100%" }}>
      {children}
    </div>
  );
};
